import { computed, toValue, type MaybeRefOrGetter } from 'vue'

import { useMutation, useQuery } from '@tanstack/vue-query'

import { TaxService } from '../services/tax.service'

import type {
  AmountSummaryRequest,
  AmountSummaryResponse,
  CountryStateInfo,
  TaxAutomaticConfig,
} from '../types/payment.types'

/**
 * TanStack-driven access to tax-related endpoints.
 *
 * - `amountSummaryMutation`: fires on user-driven events (modal open, debounced
 *   address-preview-change, "Continue") rather than reactively from a stable key.
 *   The PaymentModal owns the staleness guard via a request-id counter — no
 *   react-query cache is needed here.
 * - `fetchCountryStatesQuery(enabled)`: cacheable static reference data with an
 *   infinite staleTime — country/state lists don't change during a session, so
 *   one fetch per page load is enough. Fetched when the address step opens.
 * - `fetchTaxAutomaticConfigQuery(locationId)`: location-level automatic-tax
 *   flag. Keyed on `locationId` and cached for the full session (`staleTime:
 *   Infinity`) — switching locations re-fetches automatically. `retry: false`
 *   keeps the failure semantics silent so checkout never blocks on this call;
 *   `data` stays undefined on error, which downstream consumers read as
 *   `automaticTaxesEnabled = false`.
 */
export const useTaxQueryStore = () => {
  const service = new TaxService()

  const queryKeys = {
    countryStates: () => ['payment-tax', 'country-states'] as const,
    automaticConfig: (locationId: string | undefined) => ['payment-tax', 'automatic-config', locationId] as const,
  }

  const amountSummaryMutation = useMutation<AmountSummaryResponse, Error, AmountSummaryRequest>({
    mutationFn: payload => service.getAmountSummary(payload),
  })

  const fetchCountryStatesQuery = (enabled: MaybeRefOrGetter<boolean> = true) => {
    return useQuery<{ data: CountryStateInfo[] }, Error>({
      queryKey: queryKeys.countryStates(),
      queryFn: () => service.getCountryState(),
      staleTime: Infinity,
      gcTime: Infinity,
      enabled: computed(() => Boolean(toValue(enabled))),
    })
  }

  const fetchTaxAutomaticConfigQuery = (locationId: MaybeRefOrGetter<string | undefined>) => {
    return useQuery<TaxAutomaticConfig, Error>({
      queryKey: computed(() => queryKeys.automaticConfig(toValue(locationId))),
      queryFn: () => service.getTaxAutomaticConfig(toValue(locationId)!),
      staleTime: Infinity,
      gcTime: Infinity,
      enabled: computed(() => Boolean(toValue(locationId))),
      retry: false,
    })
  }

  return {
    queryKeys,
    amountSummaryMutation,
    fetchCountryStatesQuery,
    fetchTaxAutomaticConfigQuery,
  }
}
